/**
 * AdvancedSearch - Розширений пошук з regex, пошуком по props/states/events
 */

import {
    ApiOutlined,
    CodeOutlined,
    DatabaseOutlined,
    InfoCircleOutlined,
    SearchOutlined,
    ThunderboltOutlined
} from '@ant-design/icons'
import {
    Alert,
    Badge,
    Collapse,
    Input,
    Radio,
    Space,
    Switch,
    Tooltip,
    Typography
} from 'antd'
import React, { useMemo, useState } from 'react'
import type { ProfilerComponentInfo } from '../../../Managers/Profiler/ProfilerManager'

const { Text } = Typography
const { TextArea } = Input

interface AdvancedSearchProps {
    components: ProfilerComponentInfo[]
    onFilterChange: (filtered: ProfilerComponentInfo[]) => void
}

type SearchMode = 'simple' | 'regex' | 'props' | 'states' | 'events'

/**
 * Компонент розширеного пошуку
 */
export const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
    components,
    onFilterChange,
}) => {
    const [searchMode, setSearchMode] = useState<SearchMode>('simple')
    const [searchText, setSearchText] = useState('')
    const [caseSensitive, setCaseSensitive] = useState(false)
    const [propsKey, setPropsKey] = useState('')
    const [propsValue, setPropsValue] = useState('')
    const [statesKey, setStatesKey] = useState('')
    const [statesValue, setStatesValue] = useState('')
    const [eventName, setEventName] = useState('')
    const [regexError, setRegexError] = useState<string | null>(null)

    // Простий пошук
    const simpleSearch = (comp: ProfilerComponentInfo, text: string): boolean => {
        if (!text) return true
        const searchValue = caseSensitive ? text : text.toLowerCase()
        const checkValue = (str: string) =>
            caseSensitive ? str : str.toLowerCase()

        return (
            checkValue(comp.name).includes(searchValue) ||
            checkValue(comp.id).includes(searchValue) ||
            checkValue(comp.path).includes(searchValue)
        )
    }

    // Regex пошук
    const regexSearch = (comp: ProfilerComponentInfo, pattern: string): boolean => {
        if (!pattern) return true

        try {
            const flags = caseSensitive ? '' : 'i'
            const regex = new RegExp(pattern, flags)
            setRegexError(null)

            return (
                regex.test(comp.name) ||
                regex.test(comp.id) ||
                regex.test(comp.path) ||
                regex.test(comp.type)
            )
        } catch (error) {
            setRegexError(error instanceof Error ? error.message : 'Invalid regex')
            return false
        }
    }

    // Пошук по props
    const propsSearch = (comp: ProfilerComponentInfo): boolean => {
        if (!propsKey && !propsValue) return true

        try {
            const propsStr = JSON.stringify(comp.props).toLowerCase()

            if (propsKey && propsValue) {
                // Пошук по ключу та значенню
                const keyLower = propsKey.toLowerCase()
                const valueLower = propsValue.toLowerCase()

                for (const [key, value] of Object.entries(comp.props)) {
                    if (
                        key.toLowerCase().includes(keyLower) &&
                        JSON.stringify(value).toLowerCase().includes(valueLower)
                    ) {
                        return true
                    }
                }
                return false
            } else if (propsKey) {
                // Тільки по ключу
                return Object.keys(comp.props).some((key) =>
                    key.toLowerCase().includes(propsKey.toLowerCase())
                )
            } else if (propsValue) {
                // Тільки по значенню
                return propsStr.includes(propsValue.toLowerCase())
            }

            return true
        } catch {
            return false
        }
    }

    // Пошук по states
    const statesSearch = (comp: ProfilerComponentInfo): boolean => {
        if (!statesKey && !statesValue) return true

        try {
            const statesStr = JSON.stringify(comp.states).toLowerCase()

            if (statesKey && statesValue) {
                const keyLower = statesKey.toLowerCase()
                const valueLower = statesValue.toLowerCase()

                for (const [key, value] of Object.entries(comp.states)) {
                    if (
                        key.toLowerCase().includes(keyLower) &&
                        JSON.stringify(value).toLowerCase().includes(valueLower)
                    ) {
                        return true
                    }
                }
                return false
            } else if (statesKey) {
                return Object.keys(comp.states).some((key) =>
                    key.toLowerCase().includes(statesKey.toLowerCase())
                )
            } else if (statesValue) {
                return statesStr.includes(statesValue.toLowerCase())
            }

            return true
        } catch {
            return false
        }
    }

    // Пошук по подіях
    const eventsSearch = (comp: ProfilerComponentInfo): boolean => {
        if (!eventName) return true

        const eventLower = eventName.toLowerCase()
        return comp.eventHistory.some((event) =>
            event.event.toLowerCase().includes(eventLower)
        )
    }

    // Фільтрація компонентів
    const filteredComponents = useMemo(() => {
        let filtered = components

        switch (searchMode) {
            case 'simple':
                filtered = components.filter((comp) => simpleSearch(comp, searchText))
                break
            case 'regex':
                filtered = components.filter((comp) => regexSearch(comp, searchText))
                break
            case 'props':
                filtered = components.filter(propsSearch)
                break
            case 'states':
                filtered = components.filter(statesSearch)
                break
            case 'events':
                filtered = components.filter(eventsSearch)
                break
        }

        return filtered
    }, [
        components,
        searchMode,
        searchText,
        caseSensitive,
        propsKey,
        propsValue,
        statesKey,
        statesValue,
        eventName,
    ])

    // Оновлюємо результати
    React.useEffect(() => {
        onFilterChange(filteredComponents)
    }, [filteredComponents, onFilterChange])

    // Статистика пошуку
    const searchStats = useMemo(() => {
        const total = components.length
        const found = filteredComponents.length
        const percentage = total > 0 ? ((found / total) * 100).toFixed(1) : '0'

        return { total, found, percentage }
    }, [components.length, filteredComponents.length])

    return (
        <div>
            <Collapse
                defaultActiveKey={['search']}
                items={[
                    {
                        key: 'search',
                        label: (
                            <Space>
                                <SearchOutlined />
                                <Text strong>Розширений пошук</Text>
                                <Badge
                                    count={searchStats.found}
                                    style={{ backgroundColor: '#52c41a' }}
                                    showZero
                                />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    ({searchStats.percentage}%)
                                </Text>
                            </Space>
                        ),
                        children: (
                            <Space orientation="vertical" style={{ width: '100%' }} size="middle">
                                {/* Режим пошуку */}
                                <div>
                                    <div style={{ marginBottom: 8 }}>
                                        <Text strong style={{ fontSize: '12px' }}>
                                            Режим пошуку:
                                        </Text>
                                    </div>
                                    <Radio.Group
                                        value={searchMode}
                                        onChange={(e) => setSearchMode(e.target.value)}
                                        buttonStyle="solid"
                                        size="small"
                                    >
                                        <Radio.Button value="simple">
                                            <SearchOutlined /> Простий
                                        </Radio.Button>
                                        <Radio.Button value="regex">
                                            <CodeOutlined /> Regex
                                        </Radio.Button>
                                        <Radio.Button value="props">
                                            <ApiOutlined /> Props
                                        </Radio.Button>
                                        <Radio.Button value="states">
                                            <DatabaseOutlined /> States
                                        </Radio.Button>
                                        <Radio.Button value="events">
                                            <ThunderboltOutlined /> Події
                                        </Radio.Button>
                                    </Radio.Group>
                                </div>

                                {/* Простий пошук */}
                                {searchMode === 'simple' && (
                                    <div>
                                        <Input
                                            placeholder="Пошук за назвою, ID або шляхом..."
                                            prefix={<SearchOutlined />}
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            allowClear
                                        />
                                        <div style={{ marginTop: 8 }}>
                                            <Space>
                                                <Switch
                                                    size="small"
                                                    checked={caseSensitive}
                                                    onChange={setCaseSensitive}
                                                />
                                                <Text style={{ fontSize: '12px' }}>
                                                    Враховувати регістр
                                                </Text>
                                            </Space>
                                        </div>
                                    </div>
                                )}

                                {/* Regex пошук */}
                                {searchMode === 'regex' && (
                                    <div>
                                        <Input
                                            placeholder="Регулярний вираз (наприклад: ^Button.*$)"
                                            prefix={<CodeOutlined />}
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            allowClear
                                            status={regexError ? 'error' : undefined}
                                        />
                                        {regexError && (
                                            <Alert
                                                message={regexError}
                                                type="error"
                                                showIcon
                                                closable
                                                style={{ marginTop: 8 }}
                                            />
                                        )}
                                        <div style={{ marginTop: 8 }}>
                                            <Space>
                                                <Switch
                                                    size="small"
                                                    checked={caseSensitive}
                                                    onChange={setCaseSensitive}
                                                />
                                                <Text style={{ fontSize: '12px' }}>
                                                    Case sensitive
                                                </Text>
                                                <Tooltip title="Приклади: ^User.*, .*Button$, (Template|Component)">
                                                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                                </Tooltip>
                                            </Space>
                                        </div>
                                    </div>
                                )}

                                {/* Пошук по Props */}
                                {searchMode === 'props' && (
                                    <div>
                                        <Space orientation="vertical" style={{ width: '100%' }}>
                                            <Input
                                                placeholder="Ключ props (наприклад: userId)"
                                                prefix={<ApiOutlined />}
                                                value={propsKey}
                                                onChange={(e) => setPropsKey(e.target.value)}
                                                allowClear
                                            />
                                            <Input
                                                placeholder="Значення props (наприклад: 123)"
                                                prefix={<SearchOutlined />}
                                                value={propsValue}
                                                onChange={(e) => setPropsValue(e.target.value)}
                                                allowClear
                                            />
                                        </Space>
                                        <Alert
                                            message="Пошук працює частковим співпадінням"
                                            type="info"
                                            showIcon
                                            style={{ marginTop: 8, fontSize: '12px' }}
                                        />
                                    </div>
                                )}

                                {/* Пошук по States */}
                                {searchMode === 'states' && (
                                    <div>
                                        <Space orientation="vertical" style={{ width: '100%' }}>
                                            <Input
                                                placeholder="Ключ state (наприклад: isLoading)"
                                                prefix={<DatabaseOutlined />}
                                                value={statesKey}
                                                onChange={(e) => setStatesKey(e.target.value)}
                                                allowClear
                                            />
                                            <Input
                                                placeholder="Значення state (наприклад: true)"
                                                prefix={<SearchOutlined />}
                                                value={statesValue}
                                                onChange={(e) => setStatesValue(e.target.value)}
                                                allowClear
                                            />
                                        </Space>
                                        <Alert
                                            message="Пошук працює частковим співпадінням"
                                            type="info"
                                            showIcon
                                            style={{ marginTop: 8, fontSize: '12px' }}
                                        />
                                    </div>
                                )}

                                {/* Пошук по подіях */}
                                {searchMode === 'events' && (
                                    <div>
                                        <Input
                                            placeholder="Назва події (наприклад: click, mount, update)"
                                            prefix={<ThunderboltOutlined />}
                                            value={eventName}
                                            onChange={(e) => setEventName(e.target.value)}
                                            allowClear
                                        />
                                        <Alert
                                            message="Знайде компоненти з вказаною подією в історії"
                                            type="info"
                                            showIcon
                                            style={{ marginTop: 8, fontSize: '12px' }}
                                        />
                                    </div>
                                )}

                                {/* Результати */}
                                <div
                                    style={{
                                        padding: 12,
                                        background: '#fafafa',
                                        borderRadius: 4,
                                        marginTop: 8,
                                    }}
                                >
                                    <Space size={16}>
                                        <div>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                Знайдено:
                                            </Text>
                                            <Text
                                                strong
                                                style={{
                                                    marginLeft: 8,
                                                    fontSize: '16px',
                                                    color: '#52c41a',
                                                }}
                                            >
                                                {searchStats.found}
                                            </Text>
                                        </div>
                                        <div>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                Всього:
                                            </Text>
                                            <Text
                                                strong
                                                style={{ marginLeft: 8, fontSize: '16px' }}
                                            >
                                                {searchStats.total}
                                            </Text>
                                        </div>
                                        <div>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                Відсоток:
                                            </Text>
                                            <Text
                                                strong
                                                style={{
                                                    marginLeft: 8,
                                                    fontSize: '16px',
                                                    color: '#1890ff',
                                                }}
                                            >
                                                {searchStats.percentage}%
                                            </Text>
                                        </div>
                                    </Space>
                                </div>
                            </Space>
                        ),
                    },
                ]}
            />
        </div>
    )
}
